/**
 * Network and error handling utilities
 */

import { getTranslationKey } from 'helpers/texting';
import { toast } from 'react-toastify';

export class RequestError extends Error {
  public code: number;

  public notificationShown: boolean;

  public canceled: boolean;

  constructor(message: string, code: number, canceled = false, notificationShown = false) {
    super(message);
    this.name = 'Request Error';
    this.code = code;
    this.canceled = canceled;
    this.notificationShown = notificationShown;
  }
}

/**
 * Checks if the error or response indicates an offline/network error
 * @param errorOrResponse - The error object or API response
 * @returns true if it's a network/offline error, false otherwise
 */
export const isNetworkError = (errorOrResponse: any): boolean => {
  if (!errorOrResponse) return true;

  // Check browser online status
  if (!navigator.onLine) return true;

  // Check for network error messages
  const message = errorOrResponse.message || '';
  const errorMessagesArr = ['Network Error', 'network', 'Failed to fetch'];
  if (errorMessagesArr.some((errorMessage) => message.includes(errorMessage))) {
    return true;
  }

  // Check for network error codes (0 typically means network error)
  if (errorOrResponse.code === 0) return true;

  return false;
};

// The axios response interceptor replaces the raw error with a RequestError, which carries the
// HTTP status on `code` and has no `response`. Use this instead of reaching for `e.response.status`.
export const getErrorStatus = (error: any): number | undefined => {
  const status = error?.response?.status ?? error?.code;

  return typeof status === 'number' && status > 0 ? status : undefined;
};

export const normalizeRequestError = (error: any): RequestError => {
  if (error instanceof RequestError) return error;

  const responseCode = error?.response?.status;
  const errorCode = typeof error?.code === 'number' ? error.code : responseCode;
  const isAxiosNetworkError = error?.isAxiosError && !error?.response;
  const code = typeof errorCode === 'number' ? errorCode : isAxiosNetworkError ? 0 : -1;
  const canceled = error?.code === 'ERR_CANCELED' || error?.name === 'CanceledError';

  return new RequestError(error?.message || 'Request failed', code, canceled);
};

export const handleRequestError = (
  error: any,
  { showFallback = false }: { showFallback?: boolean } = {}
): RequestError => {
  const requestError = normalizeRequestError(error);

  if (requestError.notificationShown || requestError.canceled) return requestError;

  const isOffline = isNetworkError(requestError);
  const isServerError = requestError.code >= 500 && requestError.code < 600;

  if (!isOffline && !isServerError && !showFallback) return requestError;

  toast.error(getTranslationKey(isOffline ? 'offlineMessage' : 'errorMessage'), {
    theme: 'colored',
    autoClose: 2000,
    hideProgressBar: true,
    toastId: isOffline ? 'request-offline-error' : 'request-server-error'
  });
  requestError.notificationShown = true;

  return requestError;
};
